import React, { useState, useEffect, useCallback } from 'react';

import { useAlert } from '../../../../contexts/AlertContext';
import { seatMapApi } from '../../../../services/api';
import SeatsioDesignerWidget from '../../../../components/SeatsioDesignerWidget';
import { Plus, Search, Map, Edit2, Trash2, Copy } from 'lucide-react';
import './EventManagementPanel.css'; // Reuse styles or create new CSS

const SeatMapsPanel = () => {
    const { showAlert, showPrompt } = useAlert();

    const [charts, setCharts] = useState([]);
    const [filteredCharts, setFilteredCharts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDesigner, setShowDesigner] = useState(false);
    const [selectedChartKey, setSelectedChartKey] = useState(null);



    const loadCharts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await seatMapApi.listCharts();
            // Unwrap response (ApiResponse or direct array depending on implementation)
            const data = response.data?.data || response.data || response;
            setCharts(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading seat maps:', error);
            showAlert({ title: 'Error', message: 'Failed to load seat maps.' });
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    // Initial Load
    useEffect(() => {
        loadCharts();
    }, [loadCharts]);

    // Filter Logic
    const filterCharts = useCallback(() => {
        let filtered = [...charts];
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(chart =>
                chart.name?.toLowerCase().includes(query) ||
                chart.key?.toLowerCase().includes(query)
            );
        }
        setFilteredCharts(filtered);
    }, [charts, searchQuery]);

    useEffect(() => {
        filterCharts();
    }, [filterCharts]);

    // Helper for async prompt
    const promptAsync = (title, message, defaultValue = '', placeholder = '') => {
        return new Promise((resolve) => {
            showPrompt({
                title,
                message,
                defaultValue,
                placeholder,
                confirmLabel: 'Submit',
                onConfirm: (value) => resolve(value),
                onCancel: () => resolve(null)
            });
        });
    };

    // Actions
    const handleCreate = async () => {
        const name = await promptAsync("New Seat Map", "Enter a name for the new seat map:", "New Seat Map");
        if (!name) return;

        try {
            setLoading(true);
            const response = await seatMapApi.createChart(name);
            const chart = response.data?.data || response.data;

            if (chart && chart.key) {
                // Determine if chart key is in .key or .key() depending on object (likely .key prop in JSON)
                setSelectedChartKey(chart.key);
                setShowDesigner(true);
                // Refresh list to include new chart
                loadCharts();
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (error) {
            console.error("Failed to create chart:", error);
            showAlert({ title: "Error", message: "Failed to create seat map." });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (chart) => {
        setSelectedChartKey(chart.key);
        setShowDesigner(true);
    };

    const handleDuplicate = async (chart) => {
        const newName = await promptAsync("Duplicate Seat Map", `Enter name for copy of "${chart.name}": `, `${chart.name} (Copy)`);

        if (newName) {
            duplicateChart(chart.key, newName);
        }
    };

    const duplicateChart = async (key, newName) => {
        try {
            setLoading(true);
            await seatMapApi.duplicateChart(key, newName);
            showAlert({ title: 'Success', message: 'Seat map duplicated.' });
            loadCharts();
        } catch (error) {
            console.error('Duplicate failed:', error);
            showAlert({ title: 'Error', message: 'Failed to duplicate seat map.' });
            setLoading(false);
        }
    };

    const handleDelete = (chart) => {
        showAlert({
            title: 'Delete Seat Map?',
            message: `Are you sure you want to delete "${chart.name}" ? This cannot be undone.`,
            primaryLabel: 'Delete',
            secondaryLabel: 'Cancel',
            onPrimary: async () => {
                try {
                    setLoading(true);
                    await seatMapApi.deleteChart(chart.key);
                    loadCharts();
                } catch (error) {
                    console.error('Delete failed:', error);
                    showAlert({ title: 'Error', message: 'Failed to delete seat map.' });
                    setLoading(false);
                }
            }
        });
    };

    const handleDesignerComplete = () => {
        setShowDesigner(false);
        loadCharts();
    };

    if (showDesigner) {
        return (
            <div className="admin-events-panel" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', overflow: 'hidden' }}>

                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <SeatsioDesignerWidget
                        chartKey={selectedChartKey}
                        onDesignComplete={handleDesignerComplete}
                        onCancel={() => setShowDesigner(false)}
                    />
                </div>
            </div>
        );
    }

    if (loading && !charts.length) {
        return (
            <div className="admin-events-panel">
                <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
                    Loading seat maps...
                </div>
            </div>
        );
    }

    return (
        <div className="admin-events-panel">
            <div className="events-filter-bar">
                <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
                    <input
                        className="events-search-input"
                        type="text"
                        placeholder="Search maps..."
                        style={{ paddingLeft: '38px' }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <button
                    className="events-action-button"
                    data-variant="primary"
                    onClick={handleCreate}
                >
                    <Plus size={16} /> Create New Map
                </button>
            </div>

            <div className="events-card-grid">
                {filteredCharts.map(chart => (
                    <div className="events-card" key={chart.key}>
                        <div className="events-card-header">
                            <div className="event-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Map size={18} color="#aaa" />
                                {chart.name}
                            </div>
                            <div className="event-status">
                                <span className={chart.archived ? 'status-badge completed' : 'status-badge live'}>
                                    {chart.status || (chart.archived ? 'Archived' : 'Active')}
                                </span>
                            </div>
                        </div>
                        <div className="events-card-body">
                            <div className="event-description">
                                Key: {chart.key} <br />
                                <small style={{ opacity: 0.7 }}>Last updated: {new Date(chart.modDate || Date.now()).toLocaleDateString()}</small>
                            </div>

                            <div className="events-action-bar" style={{ marginTop: 'auto' }}>
                                <button
                                    className="events-action-button"
                                    data-variant="secondary"
                                    onClick={() => handleEdit(chart)}
                                >
                                    <Edit2 size={14} /> Edit
                                </button>
                                <button
                                    className="events-action-button"
                                    data-variant="secondary"
                                    onClick={() => handleDuplicate(chart)}
                                >
                                    <Copy size={14} /> Duplicate
                                </button>
                                <button
                                    className="events-action-button"
                                    data-variant="danger"
                                    onClick={() => handleDelete(chart)}
                                >
                                    <Trash2 size={14} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {filteredCharts.length === 0 && !loading && (
                    <div style={{ gridColumn: '1 / -1', padding: '3rem', textAlign: 'center', color: '#666' }}>
                        No seat maps found.
                    </div>
                )}
            </div>
        </div>
    );
};

export default SeatMapsPanel;
